import type { EventDatePresetToken } from './eventDatePresets';

/**
 * UI filter state for the Events List Page.
 * - `hubKey`, `venueIds` and `datePresets` are sent straight to the server as the
 *   `hubKey`/`venueId`/`datePreset` params already accepted by `/events` and `/events/dates`.
 * - `categories` (calendar-category values) stays client-side: the `/events` API only supports
 *   a single exact-match category and `/events/categories` isn't date-aware (hub-scoped only),
 *   so the day↔category coupling is computed in the UI layer over the day-bucketed events (see
 *   `facets.ts`). An empty array means "no filter applied" on that facet.
 *
 * - `membersOnly` keeps only Members Only events — sent as `visibility` (see `visibilityParam`) to
 *   `/events`, `/events/dates` and `/events/categories`, so the list, the rail and the pills agree.
 *
 * The rail's marked day is NOT here. It scrolls the list to a day rather than narrowing it, so it
 * is screen state, not a filter — keeping it out is what stops it raising "Clear all" or the
 * filtered empty state.
 */
export interface EventFilterState {
  hubKey?: string;
  categories: string[];
  venueIds: string[];
  datePresets: EventDatePresetToken[];
  membersOnly: boolean;
}

export const EMPTY_EVENT_FILTER: EventFilterState = {
  hubKey: undefined,
  categories: [],
  venueIds: [],
  datePresets: [],
  membersOnly: false,
};

/** `visibility` code of a members-only event (backend `EventConstants`). */
export const EVENT_VISIBILITY_MEMBERS_ONLY = 'Members Only';

/** The `visibility` request param for a "Members only" selection — `undefined` (no narrowing) when off. */
export function visibilityParam(membersOnly: boolean): string[] | undefined {
  return membersOnly ? [EVENT_VISIBILITY_MEMBERS_ONLY] : undefined;
}

/** Matches an ISO date-time with no timezone designator (naive, Europe/Lisbon wall-clock). */
const NAIVE_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

/**
 * Day key `YYYY-MM-DD` for an event's `startDateTime`, matching the shape the
 * dates facet returns so calendar selections compare directly against event days.
 *
 * The API returns naive Europe/Lisbon wall-clock times (no offset); those are
 * taken as-is (the date part) rather than reinterpreted through the device
 * timezone. A timezone-aware value is converted to Europe/Lisbon.
 */
export function toDateKey(isoDateTime?: string | null): string | undefined {
  if (!isoDateTime) return undefined;
  if (NAIVE_DATETIME.test(isoDateTime)) return isoDateTime.slice(0, 10);
  const parsed = new Date(isoDateTime);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Lisbon',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(parsed);
}

/** Safety cap on days enumerated by {@link dateKeyRange} — guards against corrupt/unbounded data.
 * Matches the backend's `EventService.MaxLitDaysPerInterval`. */
const MAX_RANGE_DAYS = 60;

/**
 * Mirrors the backend's `EventDatePresets.LocalDayEndGraceHours`: an event ending within this
 * many hours of local midnight belongs to the night that produced it, not to the day it
 * technically falls on (a club night 19:30 → 01:00 is one day, not two). Keeping this in sync
 * with the backend constant is what makes the default (no day selected) list agree with which
 * days the calendar rail lights up for the same event.
 */
const LOCAL_DAY_END_GRACE_HOURS = 6;

/** `endDateTime`'s day key after pulling it back by the grace window (see {@link LOCAL_DAY_END_GRACE_HOURS}). */
function graceAdjustedEndDateKey(endIso: string): string | undefined {
  if (NAIVE_DATETIME.test(endIso)) {
    const anchored = new Date(`${endIso}Z`);
    if (Number.isNaN(anchored.getTime())) return undefined;
    anchored.setUTCHours(anchored.getUTCHours() - LOCAL_DAY_END_GRACE_HOURS);
    return anchored.toISOString().slice(0, 10);
  }
  const parsed = new Date(endIso);
  if (Number.isNaN(parsed.getTime())) return undefined;
  const shifted = new Date(parsed.getTime() - LOCAL_DAY_END_GRACE_HOURS * 60 * 60 * 1000);
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Lisbon',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(shifted);
}

/**
 * Inclusive list of `YYYY-MM-DD` day keys an event spans, from `startDateTime` to `endDateTime`.
 * Falls back to a single-day key when there's no end (or it doesn't extend past the start day,
 * once the grace window is applied) — the same result `toDateKey(startDateTime)` alone would have
 * produced, so existing single-day events are unaffected.
 */
export function dateKeyRange(startIso?: string | null, endIso?: string | null): string[] {
  const startKey = toDateKey(startIso);
  if (!startKey) return [];
  const endKey = endIso ? graceAdjustedEndDateKey(endIso) : undefined;
  if (!endKey || endKey <= startKey) return [startKey];

  const [sy, sm, sd] = startKey.split('-').map(Number);
  const [ey, em, ed] = endKey.split('-').map(Number);
  const cursor = new Date(Date.UTC(sy, sm - 1, sd));
  const end = new Date(Date.UTC(ey, em - 1, ed));
  const keys: string[] = [];
  let safety = 0;
  while (cursor <= end && safety < MAX_RANGE_DAYS) {
    keys.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
    safety += 1;
  }
  return keys;
}
